import { Injectable } from '@angular/core';
import { IMenuItem, MainMenuService } from '@app/views/components/mainmenu/mainmenu.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { SideDrawerComponent } from '../side-drawer-container/side-drawer/side-drawer.component';
import { RememberService } from '@app/core/services/remember.service';
import { Base64UrlService } from '@app/services/base64url.service';
import { GraphAuthService } from '@app/modules/microsoft-graph/auth/services/graph-auth.service';
import { MenuItem } from '@app/core/services/datatask/data-task-0-to-99.service';

@Injectable({
    providedIn: 'root',
})
export class SideNavService {
    public logo$ = new BehaviorSubject<string>('');

    public name$ = new BehaviorSubject<string>('');

    public company$ = new BehaviorSubject<string>('');

    public open$ = new BehaviorSubject<boolean>(true);

    public allSideNavItems$ = new BehaviorSubject<IMenuItem[]>([]);

    public rootSideNavItems$ = new BehaviorSubject<(MenuItem & { expanded: boolean })[]>([]);

    public drawer?: SideDrawerComponent;

    public notificationsDrawer?: SideDrawerComponent | null;

    constructor(
        private mainmenu: MainMenuService,
        private remember: RememberService,
        private base64: Base64UrlService,
        private graphAuth: GraphAuthService,
    ) {}

    public getChildren(id: string | number, items: any[]): IMenuItem[] {
        const children = items.filter((item) => '' + item.parent_id === '' + id);
        return children;
    }

    public toggle() {
        const newValue = !this.open$.value;

        this.open$.next(newValue);
        const screen = window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop';
        this.remember.remember(`w_mainmenu_navbar.sidebar_open.${screen}`, newValue === true ? '1' : '0');
    }

    public setState(value: '1' | '0' | 1 | 0 | boolean | `${boolean}`) {
        if (['1', 1, true, 'true'].includes(value)) {
            this.open$.next(true);
        } else {
            this.open$.next(false);
        }
    }

    private async loadRootSideNavItems() {
        const [menus, expanded] = await Promise.all([this.mainmenu.getMainMenuMenus(), this.getExpandedItems()]);

        this.rootSideNavItems$.next(
            menus.map((menu) => {
                return { ...menu, expanded: expanded.includes('' + menu.item_id) ?? false };
            }),
        );
    }

    public setSideNavDrawer(sideDrawer: SideDrawerComponent) {
        this.drawer = sideDrawer;
    }

    public setNotificationsDrawer(sideDrawer: SideDrawerComponent | null) {
        this.notificationsDrawer = sideDrawer;
    }

    public async getExpandedItems(): Promise<string[]> {
        try {
            const value = (await this.remember.getLastStatus('tt_sidenav.itemsexpanded'))[0].variablevalue;

            if (!!value && typeof value === 'string') {
                const decodedValue = this.base64.urlDecode(value);

                return JSON.parse(decodedValue);
            }
        } catch (error) {
            console.error(error);
        }

        return [];
    }

    public async init() {
        try {
            await this.graphAuth.getAccount();
        } catch (error) {
            // console.error(error);
        }
        const [awaitAll] = await Promise.all([this.mainmenu.listWebPageMenuItems(), this.loadRootSideNavItems()]);
        this.rootSideNavItems$.subscribe({
            next: async (root) => {
                const all = awaitAll.flat(2);
                const menuIds = root.map((menu) => '' + menu.item_id);

                this.allSideNavItems$.next(
                    all
                        .filter((menu) => menu.parent_id !== null && !menuIds.includes('' + menu.item_id) && menuIds.includes('' + menu.parent_id))
                        .map((menu) => {
                            if (menu.item_glyphicon === 'glyphicon-none') {
                                return { ...menu, item_glyphicon: 'far fa-arrow-right' };
                            } else {
                                return { ...menu };
                            }
                        }),
                );
            },
        });
    }
}
